import React from 'react';
import { Calendar, Clock, X } from 'lucide-react';
import { RecurrencePattern } from '@/types/meeting';

interface RecurrenceFormProps {
  value?: RecurrencePattern;
  onChange: (pattern: RecurrencePattern | undefined) => void;
  onClose: () => void;
}

export function RecurrenceForm({ value, onChange, onClose }: RecurrenceFormProps) {
  const [pattern, setPattern] = React.useState<RecurrencePattern>(
    value || {
      frequency: 'WEEKLY',
      interval: 1,
      daysOfWeek: [],
    }
  );

  const handleChange = (updates: Partial<RecurrencePattern>) => {
    const updatedPattern = { ...pattern, ...updates };
    setPattern(updatedPattern);
    onChange(updatedPattern);
  };

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Recurring Meeting</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Frequency</label>
          <select
            value={pattern.frequency}
            onChange={(e) => handleChange({ frequency: e.target.value as RecurrencePattern['frequency'] })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white ring-1 ring-gray-200 hover:ring-gray-300 transition-shadow px-4 py-3"
          >
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Repeat every
          </label>
          <div className="mt-1 flex items-center space-x-2">
            <input
              type="number"
              min="1"
              value={pattern.interval}
              onChange={(e) => handleChange({ interval: parseInt(e.target.value) })}
              className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white ring-1 ring-gray-200 hover:ring-gray-300 transition-shadow px-4 py-3"
            />
            <span className="text-gray-500">
              {pattern.frequency.toLowerCase()}
              {pattern.interval > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {pattern.frequency === 'WEEKLY' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repeat on
            </label>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const currentDays = pattern.daysOfWeek || [];
                    const newDays = currentDays.includes(index)
                      ? currentDays.filter((d) => d !== index)
                      : [...currentDays, index];
                    handleChange({ daysOfWeek: newDays });
                  }}
                  className={`
                    px-3 py-1 rounded-full text-sm font-medium
                    ${
                      pattern.daysOfWeek?.includes(index)
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }
                  `}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">End</label>
          <div className="mt-2 space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                name="endType"
                checked={!pattern.endDate && !pattern.occurrences}
                onChange={() => handleChange({ endDate: undefined, occurrences: undefined })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <label className="ml-2 text-sm text-gray-700">Never</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                name="endType"
                checked={!!pattern.occurrences}
                onChange={() => handleChange({ occurrences: 10, endDate: undefined })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <label className="text-sm text-gray-700">After</label>
              {pattern.occurrences !== undefined && (
                <input
                  type="number"
                  min="1"
                  value={pattern.occurrences}
                  onChange={(e) => handleChange({ occurrences: parseInt(e.target.value) })}
                  className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white ring-1 ring-gray-200 hover:ring-gray-300 transition-shadow px-4 py-3"
                />
              )}
              <span className="text-sm text-gray-700">occurrences</span>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                name="endType"
                checked={!!pattern.endDate}
                onChange={() => handleChange({ 
                  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                  occurrences: undefined 
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <label className="text-sm text-gray-700">On</label>
              {pattern.endDate && (
                <input
                  type="date"
                  value={pattern.endDate.toISOString().split('T')[0]}
                  onChange={(e) => handleChange({ endDate: new Date(e.target.value) })}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white ring-1 ring-gray-200 hover:ring-gray-300 transition-shadow px-4 py-3"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}